<template>
    <div>
        <div v-if="!noButton" ref="adyen-dropin"></div>

        <v-dialog v-model="displayEditAddressRequest" width="550">
            <v-card>
                <v-card-text>
                    {{ 'To pay with Klarna you need to fill in house number in a separate field.' | translation }}
                </v-card-text>

                <v-card-actions>
                    <v-btn @click="displayEditAddressRequest = false">
                        {{ 'Select another payment method' | translation }}
                    </v-btn>
                    <v-btn color="primary" @click="$emit('editAddress', 'drop-in')" style="flex: 1">
                        {{ 'Edit billing address' | translation }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import { AdyenPaymentType } from './config/adyen-payment-type-config';
import storeModule from './store/adyenPayment';

export const adyenModuleName = 'AdyenPaymentType';

export default {
    name: adyenModuleName,
    props: {
        noButton: Boolean,
    },
    data() {
        return {
            displayEditAddressRequest: false,
        };
    },
    created() {
        const { transactionId, payload, redirectResult, resultCode, error } = this.$route.query;
        if (transactionId && (payload || redirectResult)) {
            this.paymentConfirm(
                {
                    payload,
                    redirectResult,
                },
                transactionId,
            );
        } else if (transactionId && resultCode) {
            this.onPaymentConfirmed(resultCode, transactionId);
        } else if (error) {
            this.setError(error);
        }
    },
    mounted() {
        if (!this.noButton) {
            this.setError(null);

            if (typeof AdyenCheckout === 'undefined') {
                const script = document.createElement('script');
                script.src = 'https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/3.19.0/adyen.js';
                document
                    .getElementsByTagName('script')[0]
                    .parentNode.insertBefore(script, document.getElementsByTagName('script')[0]);

                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = 'https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/3.19.0/adyen.css';
                document
                    .getElementsByTagName('link')[0]
                    .parentNode.insertBefore(link, document.getElementsByTagName('link')[0]);

                script.addEventListener('load', () => {
                    this.$nextTick(() => {
                        this.initPaymentForm();
                    });
                });
            } else {
                this.$nextTick(() => {
                    this.initPaymentForm();
                });
            }
        }
    },
    computed: {
        ...mapState('Payment', ['paymentTypeSelected']),
        ...mapState(adyenModuleName, ['loading']),
        ...mapState('Coupon', ['paidCouponAmount']),
        ...mapGetters('Payment', ['calculateOrderPaymentFee']),
    },
    methods: {
        ...mapActions(adyenModuleName, ['loadPaymentConfiguration', 'executePayment', 'confirmPayment']),
        ...mapMutations(adyenModuleName, ['setLoading']),
        ...mapMutations('Payment', ['setError']),
        initPaymentForm() {
            const me = this;
            const country = this.$order.getShippingAddress().country;
            const language = this.$language.getLanguage();
            const currency = this.$currency.getSelected();
            const basketTotal = this.$basket.getBasketTotal();
            return this.loadPaymentConfiguration({
                basketTotal,
                country,
                currency,
                language,
            }).then(paymentConfig => {
                if (paymentConfig) {
                    const configuration = {
                        openFirstPaymentMethod: false,
                        locale: language.toLowerCase() + '-' + language.toUpperCase(),
                        environment: paymentConfig.isLiveEnv ? 'live' : 'test',
                        clientKey: paymentConfig.clientKey,
                        paymentMethodsResponse: paymentConfig.paymentMethods,
                        paymentMethodsConfiguration: {
                            card: {
                                hasHolderName: true,
                                holderNameRequired: true,
                                enableStoreDetails: true,
                                hideCVC: false,
                            },
                        },
                        onSubmit(state) {
                            if (!me.loading) {
                                if (
                                    state.data &&
                                    state.data.paymentMethod &&
                                    (state.data.paymentMethod.type === 'klarna' ||
                                        state.data.paymentMethod.type === 'klarna_account')
                                ) {
                                    if (!me.$order.getShippingAddress().houseNumber) {
                                        me.displayEditAddressRequest = true;
                                    } else {
                                        me.paymentProcess(state);
                                    }
                                } else {
                                    me.paymentProcess(state);
                                }
                            }
                        },
                        onAdditionalDetails(state, dropin) {
                            me.paymentConfirm({
                                payload: state.data,
                            })
                                .then(action => {
                                    dropin.handleAction(action);
                                })
                                .catch(error => {
                                    me.setError(error.toString());
                                });
                        },
                    };

                    if (paymentConfig.fees) {
                        for (const paymentMethod of paymentConfig.paymentMethods.paymentMethods) {
                            if (paymentConfig.fees[paymentMethod.type]) {
                                const type = paymentMethod.type === 'scheme' ? 'card' : paymentMethod.type;
                                let fee = paymentConfig.fees[paymentMethod.type];
                                const paymentNameParts = [];
                                paymentNameParts.push(paymentMethod.name);
                                paymentNameParts.push(' ');
                                paymentNameParts.push('(');
                                paymentNameParts.push(this.$language.translate('fee'));
                                paymentNameParts.push(' ');
                                if (fee.includes('%')) {
                                    paymentNameParts.push(`${fee} = `);
                                }
                                if (this.paidCouponAmount) {
                                    fee = this.calculateOrderPaymentFee(basketTotal - this.paidCouponAmount, fee);
                                } else {
                                    fee = this.calculateOrderPaymentFee(basketTotal, fee);
                                }
                                paymentNameParts.push(this.$root.$options.filters.currency(fee));
                                paymentNameParts.push(')');
                                const paymentName = paymentNameParts.join('');
                                if (configuration.paymentMethodsConfiguration[type]) {
                                    configuration.paymentMethodsConfiguration[type].name = paymentName;
                                } else {
                                    configuration.paymentMethodsConfiguration[type] = {
                                        name: paymentName,
                                    };
                                }
                            }
                        }
                    }

                    this.checkout = new AdyenCheckout(configuration);
                    this.checkout.create('dropin').mount(this.$refs['adyen-dropin']);
                }
            });
        },

        paymentProcess(state) {
            if (state.isValid && typeof state.data === 'object') {
                this.setLoading('setLoading', true);
                this.setError(null);

                this.$order.getOrder().then(order => {
                    if (order && order.id) {
                        const paymentData = state.data;

                        if (
                            paymentData &&
                            paymentData.paymentMethod &&
                            (paymentData.paymentMethod.type === 'klarna' ||
                                paymentData.paymentMethod.type === 'klarna_account')
                        ) {
                            paymentData.shopperLocale = navigator.language.replace('-', '_');
                        }

                        this.executePayment({
                            orderId: parseInt(order.id),
                            transactionId: order.transaction_id,
                            paymentData,
                            paymentMethod: this.paymentTypeSelected,
                            language: this.$language.getLanguage(),
                        }).then(result => {
                            if (result) {
                                this.setLoading('setLoading', false);
                                this.$emit('success', order);
                            }
                        });
                    } else {
                        this.setLoading('setLoading', false);
                        this.setError('Order not created, please try again later.');
                    }
                });
            }
        },
        paymentConfirm(paymentData, transactionId) {
            return this.confirmPayment({
                paymentData,
                transactionId,
            })
                .then(result => {
                    if (result && result.resultCode) {
                        this.onPaymentConfirmed(result.resultCode, transactionId);
                    }
                })
                .catch(err => {
                    if (err === 'cancelled') {
                        this.$emit('cancel');
                    }
                });
        },
        onPaymentConfirmed(resultCode, transactionId) {
            if (resultCode === 'Authorised' || resultCode === 'Pending' || resultCode === 'Received') {
                if (resultCode === 'Authorised') {
                    this.$emit('success', { transaction_id: transactionId });
                } else {
                    this.$emit('received', { transaction_id: transactionId });
                }
            } else if (resultCode === 'Refused') {
                this.setError('Transaction is refused');
                this.$emit('cancel');
            }
        },
    },
    install(Vue) {
        if (!Vue.$store.state[adyenModuleName]) {
            Vue.$store.registerModule(adyenModuleName, storeModule);
        }
        Vue.component(adyenModuleName, this);
        Vue.$store.commit('Payment/addPaymentType', new AdyenPaymentType());
    },
};
</script>
